// File: packages/auth/src/hooks.ts
// Version: 2026.4.1 | ForgeTrack auth hooks

import { useContext, useEffect, useMemo } from 'react';
import { AuthContext } from './provider';
import type { AuthContextType } from './types';
import { type ForgeTrackRole, hasAnyRole, hasMinimumRole } from './roles';

/**
 * Primary auth hook. Use this in every component that needs auth state.
 * NEVER import from @azure/msal-react directly — always use this hook.
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a ForgeTrackAuthProvider');
  }
  return context;
}

/**
 * Role-based access hook. Returns the user's roles and helper functions.
 */
export function useForgeTrackRoles() {
  const { account } = useAuth();

  const roles = useMemo<string[]>(() => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;
    const rolesClaim = claims['roles'];
    if (Array.isArray(rolesClaim)) return rolesClaim as string[];
    return [];
  }, [account]);

  return {
    roles,
    hasAnyRole: (requiredRoles: ForgeTrackRole[]) => hasAnyRole(roles, requiredRoles),
    hasMinimumRole: (minimumRole: ForgeTrackRole) => hasMinimumRole(roles, minimumRole),
  };
}

/**
 * Guard hook — redirects to login if not authenticated.
 * Use at route level for protected pages.
 */
export function useRequireAuth(): AuthContextType {
  const auth = useAuth();

  useEffect(() => {
    // Only trigger login when:
    // 1. MSAL has finished initializing (handleRedirectPromise complete)
    // 2. No interaction is in progress (not already redirecting)
    // 3. User is not authenticated
    if (auth.isInitialized && !auth.isAuthenticated && !auth.isLoading) {
      auth.login();
    }
  }, [auth.isInitialized, auth.isAuthenticated, auth.isLoading, auth.login]);

  return auth;
}
